import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { api } from '../../auth/authStore';
import toast from 'react-hot-toast';

interface Client {
  id: string;
  displayName: string;
  legalName: string;
  email: string;
}

interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
  amount: number;
  taxAmount: number;
  total: number;
}

interface Invoice {
  _id: string;
  number: string;
  clientId: {
    _id: string;
    displayName: string;
  };
  issueDate: string;
  dueDate: string;
  currency: string;
  status: string;
  items: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    taxRate: number;
  }>;
  notes?: string;
  terms?: string;
}

interface InvoiceEditModalProps {
  isOpen: boolean;
  invoice: Invoice;
  onClose: () => void;
  onSuccess: () => void;
}

export const InvoiceEditModal: React.FC<InvoiceEditModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onSuccess,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    clientId: '',
    issueDate: '',
    dueDate: '',
    currency: 'CAD',
    notes: '',
    terms: '',
  });

  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);

  // Load clients when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  // Initialize form data when invoice and clients are available
  useEffect(() => {
    if (invoice && clients.length > 0) {
      // Handle both populated and non-populated clientId
      let clientId;
      if (typeof invoice.clientId === 'object' && invoice.clientId !== null) {
        // If clientId is populated (object with _id)
        clientId = invoice.clientId._id || invoice.clientId.id || invoice.clientId;
      } else {
        // If clientId is just a string ID
        clientId = invoice.clientId;
      }

      const newFormData = {
        clientId: clientId,
        issueDate: invoice.issueDate.split('T')[0],
        dueDate: invoice.dueDate.split('T')[0],
        currency: invoice.currency,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
      };

      setFormData(newFormData);

      setLineItems(
        (invoice.items || []).map((item, index) => ({
          id: (index + 1).toString(),
          description: item.description || '',
          quantity: item.quantity || 0,
          unitPrice: item.unitPrice || 0,
          taxRate: item.taxRate || 0,
          amount: (item.quantity || 0) * (item.unitPrice || 0),
          taxAmount: ((item.quantity || 0) * (item.unitPrice || 0)) * (item.taxRate || 0),
          total: ((item.quantity || 0) * (item.unitPrice || 0)) * (1 + (item.taxRate || 0)),
        }))
      );
    }
  }, [invoice, clients]);



  const fetchClients = async () => {
    try {
      const response = await api.get('/firm-admin/clients');
      setClients(response.data.data.clients || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const formatCurrency = (amount: number | undefined | null, currency: string = 'CAD') => {
    // Handle undefined, null, or NaN values
    const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;

    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(safeAmount);
  };

  const calculateLineItem = (item: Partial<InvoiceLineItem>): InvoiceLineItem => {
    // Ensure we have valid numbers
    const quantity = typeof item.quantity === 'number' && !isNaN(item.quantity) ? item.quantity : 0;
    const unitPrice = typeof item.unitPrice === 'number' && !isNaN(item.unitPrice) ? item.unitPrice : 0;
    const taxRate = typeof item.taxRate === 'number' && !isNaN(item.taxRate) ? item.taxRate : 0;

    const amount = quantity * unitPrice;
    const taxAmount = amount * taxRate;
    const total = amount + taxAmount;

    return {
      id: item.id || '',
      description: item.description || '',
      quantity,
      unitPrice,
      taxRate,
      amount: Math.round(amount * 100) / 100,
      taxAmount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
    };
  };

  const updateLineItem = (index: number, field: keyof InvoiceLineItem, value: any) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = calculateLineItem({
      ...updatedItems[index],
      [field]: value,
    });
    setLineItems(updatedItems);
  };

  const addLineItem = () => {
    const newItem: InvoiceLineItem = {
      id: (lineItems.length + 1).toString(),
      description: '',
      quantity: 1,
      unitPrice: 0,
      taxRate: 0.13,
      amount: 0,
      taxAmount: 0,
      total: 0,
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Client is required';
    }

    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }

    if (lineItems.length === 0) {
      newErrors.items = 'At least one line item is required';
    }

    lineItems.forEach((item, index) => {
      if (!item.description.trim()) {
        newErrors[`item_${index}_description`] = 'Description is required';
      }
      if (item.quantity <= 0) {
        newErrors[`item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unitPrice < 0) {
        newErrors[`item_${index}_unitPrice`] = 'Unit price cannot be negative';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const invoiceData = {
        ...formData,
        items: lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate,
        })),
      };

      await api.patch(`/firm-admin/billing/invoices/${invoice._id}`, invoiceData);
      toast.success('Ready to raise invoice updated successfully');
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Failed to update invoice:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to update invoice' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);
    const taxTotal = lineItems.reduce((sum, item) => sum + item.taxAmount, 0);
    const total = subtotal + taxTotal;
    return { subtotal, taxTotal, total };
  };

  const totals = calculateTotals();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Ready to Raise Invoice {invoice.number}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> You can edit this invoice because it's in "Ready to Raise" status.
                Once sent to the client, invoices cannot be modified.
              </p>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client *
                </label>
                <select
                  value={formData.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.clientId ? 'border-red-300' : 'border-gray-300'
                  }`}
                  disabled={invoice.status !== 'draft'}
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.displayName}
                    </option>
                  ))}
                </select>
                {errors.clientId && (
                  <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={invoice.status !== 'draft'}
                >
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="USD">USD - US Dollar</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Issue Date
                </label>
                <input
                  type="date"
                  value={formData.issueDate}
                  onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={invoice.status !== 'draft'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.dueDate ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.dueDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.dueDate}</p>
                )}
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Line Items</h3>
                <button
                  type="button"
                  onClick={addLineItem}
                  className="flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                  disabled={invoice.status !== 'draft'}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </button>
              </div>

              {errors.items && (
                <p className="mb-4 text-sm text-red-600">{errors.items}</p>
              )}

              <div className="space-y-4">
                {lineItems.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description *
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors[`item_${index}_description`] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Item description"
                          disabled={invoice.status !== 'draft'}
                        />
                        {errors[`item_${index}_description`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`item_${index}_description`]}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Quantity *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors[`item_${index}_quantity`] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          disabled={invoice.status !== 'draft'}
                        />
                        {errors[`item_${index}_quantity`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`item_${index}_quantity`]}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Unit Price *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors[`item_${index}_unitPrice`] ? 'border-red-300' : 'border-gray-300'
                          }`}
                          disabled={invoice.status !== 'draft'}
                        />
                        {errors[`item_${index}_unitPrice`] && (
                          <p className="mt-1 text-sm text-red-600">{errors[`item_${index}_unitPrice`]}</p>
                        )}
                      </div>

                      <div className="flex items-end">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tax Rate
                          </label>
                          <select
                            value={item.taxRate}
                            onChange={(e) => updateLineItem(index, 'taxRate', parseFloat(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={invoice.status !== 'draft'}
                          >
                            <option value={0}>0% (No Tax)</option>
                            <option value={0.05}>5% (GST)</option>
                            <option value={0.13}>13% (HST)</option>
                            <option value={0.15}>15% (HST)</option>
                          </select>
                        </div>
                        {lineItems.length > 1 && invoice.status === 'draft' && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="ml-2 p-2 text-red-600 hover:text-red-800"
                            title="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 text-right text-sm text-gray-600">
                      Amount: ${item.amount.toFixed(2)} | Tax: ${item.taxAmount.toFixed(2)} | Total: ${item.total.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm font-medium">${totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Tax:</span>
                    <span className="text-sm font-medium">${totals.taxTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2">
                    <span className="text-base font-semibold">Total:</span>
                    <span className="text-base font-semibold">${totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes and Terms */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Internal notes (not visible to client)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Terms & Conditions
                </label>
                <textarea
                  value={formData.terms}
                  onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Payment terms and conditions"
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || invoice.status !== 'draft'}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Updating...' : 'Update Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};
